import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { AbigailBackdrop } from "@/components/abigail/AbigailBackdrop";
import { PlatformHeader } from "@/components/abigail/PlatformHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { fetchMe, meQueryKey, type Me } from "@/lib/auth";

export const Route = createFileRoute("/_authenticated/admin/configuracoes")({
  beforeLoad: async ({ context }) => {
    const cached = context.queryClient.getQueryData<Me | null>(meQueryKey);
    const me =
      cached ??
      (await context.queryClient.fetchQuery({ queryKey: meQueryKey, queryFn: fetchMe }));
    if (!me?.admin) throw redirect({ to: "/chat" });
  },
  component: AdminConfiguracoesPage,
});

type Configuracao = {
  chave: string;
  valor: string;
  rotulo: string;
  descricao: string | null;
  tipo: "numero" | "texto" | "booleano";
  updatedAt: string;
};

const CONFIGURACOES_KEY = ["admin", "configuracoes"] as const;

function AdminConfiguracoesPage() {
  const queryClient = useQueryClient();

  const { data: configuracoes = [], isLoading } = useQuery({
    queryKey: CONFIGURACOES_KEY,
    queryFn: () => api.get<Configuracao[]>("/api/admin/configuracoes"),
  });

  return (
    <div className="relative flex min-h-dvh flex-col">
      <AbigailBackdrop dim />
      <PlatformHeader />

      <main className="flex flex-1 flex-col px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-3xl">
          <h1 className="font-serif text-ember text-3xl">Configurações</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Parâmetros da plataforma. As alterações passam a valer imediatamente, sem
            precisar reiniciar nada.
          </p>

          <div className="mt-6 flex flex-col gap-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : configuracoes.length === 0 ? (
              <p className="py-16 text-center text-muted-foreground">
                Nenhum parâmetro cadastrado.
              </p>
            ) : (
              configuracoes.map((config) => (
                <LinhaConfiguracao
                  key={config.chave}
                  config={config}
                  onSaved={() =>
                    void queryClient.invalidateQueries({ queryKey: CONFIGURACOES_KEY })
                  }
                />
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function LinhaConfiguracao({
  config,
  onSaved,
}: {
  config: Configuracao;
  onSaved: () => void;
}) {
  const [valor, setValor] = useState(config.valor);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  // A save elsewhere (or a refetch) should win over a stale local edit.
  useEffect(() => {
    setValor(config.valor);
  }, [config.valor]);

  const mutation = useMutation({
    mutationFn: (novo: string) =>
      api.patch(`/api/admin/configuracoes/${config.chave}`, { valor: novo }),
    onSuccess: () => {
      setErro(null);
      setSalvo(true);
      onSaved();
      setTimeout(() => setSalvo(false), 2000);
    },
    onError: (err) => {
      setErro(err instanceof Error ? err.message : "Não foi possível salvar.");
    },
  });

  const alterado = valor.trim() !== config.valor;

  return (
    <div className="rounded-xl border border-border bg-card/80 p-4 shadow-card-deep backdrop-blur-md">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-foreground">{config.rotulo}</p>
          {config.descricao ? (
            <p className="mt-1 text-sm text-muted-foreground">{config.descricao}</p>
          ) : null}
          <p className="mt-1 font-mono text-xs text-muted-foreground/70">{config.chave}</p>
        </div>

        {config.tipo === "booleano" ? (
          <div className="flex shrink-0 items-center gap-3">
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : null}
            <span className="text-sm text-muted-foreground">
              {valor === "true" ? "Ativado" : "Desativado"}
            </span>
            <Switch
              checked={valor === "true"}
              disabled={mutation.isPending}
              aria-label={config.rotulo}
              onCheckedChange={(marcado) => {
                const novo = marcado ? "true" : "false";
                setValor(novo);
                mutation.mutate(novo);
              }}
            />
          </div>
        ) : (
        <form
          className="flex shrink-0 items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (alterado) mutation.mutate(valor.trim());
          }}
        >
          <Input
            value={valor}
            inputMode={config.tipo === "numero" ? "numeric" : "text"}
            onChange={(event) => setValor(event.target.value)}
            className="h-9 w-28 border-border bg-background/60 text-sm"
            aria-label={config.rotulo}
          />
          <Button
            type="submit"
            size="sm"
            disabled={!alterado || mutation.isPending}
            className="bg-ember shadow-ember text-primary-foreground hover:opacity-90"
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : salvo ? (
              <Check className="h-4 w-4" />
            ) : (
              "Salvar"
            )}
          </Button>
        </form>
        )}
      </div>

      {erro ? <p className="mt-2 text-sm text-destructive">{erro}</p> : null}
    </div>
  );
}
